import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { UserTier } from '../types';

interface HeaderProps {
  isAuthenticated: boolean;
  userTier: UserTier;
  userEmail?: string;
  onSignIn: () => void;
  onSignOut: () => void;
  onUpgrade?: () => void;
  onViewHistory?: () => void;
}

/**
 * Floating header with user info and navigation
 */
export function Header({
  isAuthenticated,
  userTier,
  userEmail,
  onSignIn,
  onSignOut,
  onUpgrade,
  onViewHistory,
}: HeaderProps) {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <header className="relative z-30 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-3"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
              <svg className="w-6 h-6 text-white" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M50 15 C37 28, 25 45, 25 58 C25 70, 35 82, 50 82 C65 82, 75 70, 75 58 C75 45, 63 28, 50 15 Z" fill="white"/>
                <path d="M50 35 L50 60 M50 60 L42 52 M50 60 L58 52" stroke="#3B82F6" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">InstaDrop</h1>
              <p className="text-xs text-gray-400">instadrop.io</p>
            </div>
          </motion.div>

          {/* User Menu */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="relative"
          >
            {isAuthenticated ? (
              <>
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-4 py-2.5 hover:bg-white/20 transition-all"
                >
                  {userTier === 'subscribed' && (
                    <svg className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  )}
                  <div className="text-sm font-medium text-white">
                    {userEmail?.split('@')[0]}
                  </div>
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
                    {userEmail?.charAt(0).toUpperCase()}
                  </div>
                </button>

                <AnimatePresence>
                  {showMenu && (
                    <>
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-40"
                        onClick={() => setShowMenu(false)}
                      />
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute right-0 mt-2 w-56 bg-gray-900/95 backdrop-blur-sm border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50"
                      >
                        {userTier === 'subscribed' ? (
                          <div className="px-4 py-3 text-sm font-medium text-yellow-400 bg-yellow-400/10 border-b border-white/10 flex items-center gap-2">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                            </svg>
                            You're premium!
                          </div>
                        ) : onUpgrade && (
                          <button
                            onClick={() => {
                              // Track upgrade button click
                              if (window.umami) {
                                window.umami.track('upgrade-button', { location: 'header-menu' });
                              }
                              onUpgrade();
                              setShowMenu(false);
                            }}
                            className="w-full px-4 py-3 text-left text-sm font-medium text-white hover:bg-white/10 transition flex items-center gap-2"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                            </svg>
                            Upgrade Plan
                          </button>
                        )}
                        {onViewHistory && (
                          <button
                            onClick={() => {
                              onViewHistory();
                              setShowMenu(false);
                            }}
                            className="w-full px-4 py-3 text-left text-sm font-medium text-white hover:bg-white/10 transition flex items-center gap-2"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            View History
                          </button>
                        )}
                        <button
                          onClick={() => {
                            onSignOut();
                            setShowMenu(false);
                          }}
                          className="w-full px-4 py-3 text-left text-sm font-medium text-red-400 hover:bg-white/10 transition flex items-center gap-2 border-t border-white/10"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                          Sign Out
                        </button>
                      </motion.div>
                    </>
                  )}
                </AnimatePresence>
              </>
            ) : (
              <div className="flex flex-col items-end gap-2">
                <button
                  onClick={() => {
                    // Track upgrade button click
                    if (window.umami) {
                      window.umami.track('upgrade-button', { location: 'header-main' });
                    }
                    onSignIn();
                  }}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-medium rounded-xl px-6 py-2.5 transition-all shadow-lg"
                >
                  Upgrade
                </button>
                <div className="relative group">
                  <div className="text-xs text-gray-400 cursor-default">
                    Just $1 one-time payment to share files bigger than 10mb!
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </header>
  );
}
